/**
 * BulkUpdateDialog
 *
 * Generic dialog for bulk-updating common fields across multiple entities.
 * The fields rendered are driven entirely by a `BulkUpdateFieldConfig[]`, so a
 * single component replaces the per-entity bulk-update dialogs. Only fields the
 * user fills in are included in the emitted update payload.
 */

import { useState, useEffect, useRef } from 'react';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';
import './BulkUpdateDialog.css';

export interface BulkUpdateFieldOption {
  value: string;
  label: string;
}

export interface BulkUpdateFieldConfig {
  /** Key used in the emitted update payload. */
  key: string;
  /** Visible field label. */
  label: string;
  /** Control type: a `<select>` of options or a free-text `<input>`. */
  type: 'select' | 'text';
  /** Options for `type: 'select'`. */
  options?: BulkUpdateFieldOption[];
  /** Placeholder for `type: 'text'`. */
  placeholder?: string;
}

export interface BulkUpdateDialogProps {
  open: boolean;
  selectedCount: number;
  /** Entity noun used in the title and confirm button (e.g. `{ singular: 'service', plural: 'services' }`). */
  entityLabel: { singular: string; plural: string };
  fields: BulkUpdateFieldConfig[];
  /** Prefix for generated element ids (label `htmlFor` / control `id`). */
  idPrefix: string;
  /** Class applied to `<select>` controls so each list keeps its filter-select styling. */
  selectClassName?: string;
  onConfirm: (updates: Record<string, string>) => void;
  onCancel: () => void;
  isUpdating?: boolean;
}

export function BulkUpdateDialog({
  open,
  selectedCount,
  entityLabel,
  fields,
  idPrefix,
  selectClassName,
  onConfirm,
  onCancel,
  isUpdating = false,
}: BulkUpdateDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);
  const DialogElement = 'forge-dialog' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;
  const titleId = `${idPrefix}-title`;

  const [values, setValues] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.open = open;
    }
    if (!open) {
      setValues(new Map());
    }
  }, [open]);

  const setFieldValue = (key: string, value: string): void => {
    setValues((prev) => new Map(prev).set(key, value));
  };

  const normalize = (field: BulkUpdateFieldConfig): string => {
    const raw = values.get(field.key) ?? '';
    return field.type === 'text' ? raw.trim() : raw;
  };

  const hasChanges = fields.some((field) => normalize(field) !== '');

  const handleConfirm = (): void => {
    const updates = Object.fromEntries(
      fields
        .map((field) => [field.key, normalize(field)] as const)
        .filter(([, value]) => value !== '')
    );
    onConfirm(updates);
  };

  const noun = selectedCount === 1 ? entityLabel.singular : entityLabel.plural;

  return (
    <DialogElement
      ref={dialogRef}
      onForge-dialog-close={() => !isUpdating && onCancel()}
      className="bulk-update-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="dialog-content">
        <div className="dialog-header">
          <h2 id={titleId} className="forge-typography--headline5">
            Update {selectedCount} {noun}
          </h2>
        </div>

        <div className="dialog-body">
          <p className="forge-typography--body2 bulk-update-dialog__hint">
            Only fields you fill in will be updated. Leave a field blank to keep existing values.
          </p>

          {fields.map((field) => {
            const fieldId = `${idPrefix}-${field.key}`;
            return (
              <div className="dialog-form-field" key={field.key}>
                <label htmlFor={fieldId} className="forge-typography--label1 dialog-form-label">
                  {field.label}
                </label>
                {field.type === 'select' ? (
                  <select
                    id={fieldId}
                    className={selectClassName}
                    value={values.get(field.key) ?? ''}
                    onChange={(e) => setFieldValue(field.key, e.target.value)}
                    disabled={isUpdating}
                  >
                    <option value="">— no change —</option>
                    {field.options?.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={fieldId}
                    type="text"
                    className="dialog-form-input"
                    placeholder={field.placeholder ?? '— no change —'}
                    value={values.get(field.key) ?? ''}
                    onChange={(e) => setFieldValue(field.key, e.target.value)}
                    disabled={isUpdating}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="dialog-actions">
          <ForgeButton variant="text" onClick={onCancel} disabled={isUpdating} type="button">
            Cancel
          </ForgeButton>
          <ForgeButton
            variant="raised"
            onClick={handleConfirm}
            disabled={isUpdating || !hasChanges}
            type="button"
          >
            {isUpdating ? 'Updating...' : `Update ${selectedCount} items`}
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
